import { api } from '../utils/api';

/**
 * interpretation_object write-path client — the CPA's evidence-gated attest/clear
 * of a CP 00 30 mitigation lever. Mirrors caseProfileService: the shared `api`
 * fetch client auto-injects the Clerk token; the accountant path passes its own
 * `{ headers }`. Backend: `backend/app/api/v1/claims_api.py`.
 *
 *   GET   /api/v1/claims/{id}/interpretation           → current object + p_proven
 *   PATCH /api/v1/claims/{id}/interpretation/mitigation → attest (p=1) / clear (p=0)
 *
 * The board this moves reads the SAME p through the brief's
 * mediator_view.defense_itemization[].p (see deliberationApi.ts). No money ever
 * crosses this seam: the body is `p + cite` only and the backend fences a dollar.
 */

export type MitigationFactorId =
  | 'marketing_maintained'
  | 'temp_relocation'
  | 'alt_revenue'
  | 'expense_reduction'
  | 'resume_timeline';

export type AttestorRole = 'accountant' | 'stipulated_order';

/** One page cite — NO money. `page` is 1-based; `quote` optional. */
export interface InterpPageCite {
  doc_id: string;
  page: number;
  quote?: string | null;
}

export interface InterpAttestor {
  role: AttestorRole;
  id: string;
}

/** One mitigation lever as stored on the object (round-trips the backend model). */
export interface InterpMitigationLever {
  id: MitigationFactorId;
  p: number; // 0.0 | 1.0 in v1
  evidence_cite: InterpPageCite[];
  attestor: InterpAttestor | null;
  attested_at: string | null;
  proposed_p: number | null;
}

export interface InterpretationObject {
  report_id: string;
  schema_version: '1.0';
  branch: 'first_party' | 'third_party';
  covered_cause: string | null;
  period_start_rule: string | null;
  period_end_rule: string | null;
  mitigation: InterpMitigationLever[];
  open_questions: string[];
  facts_used: InterpPageCite[];
  engine_context: { industry_code?: string | null; naics_code?: string | null };
}

/** The exit shape fed to the mediator view: {factor: {p, basis, set_by}}. */
export type PProven = Record<string, { p: number; basis: string; set_by: string }>;

export interface InterpretationResponse {
  claim_id: string;
  interpretation: InterpretationObject | null;
  p_proven: PProven;
  /** present + true when a stored blob failed to validate (read never 500s). */
  invalid?: boolean;
}

/** PATCH body — `p + cite` only. `extra="forbid"` on the server rejects anything
 * else (proposed_p, covered_cause, a date, a dollar) with a 422. */
export interface MitigationAttestationBody {
  id: MitigationFactorId;
  /** 0 = clear, 1 = attest. On/off only in v1. */
  p: 0 | 1;
  /** Required (≥1) to attest; ignored on a clear. */
  evidence_cite: InterpPageCite[];
  /** Defaults to 'accountant'; 'stipulated_order' requires `order_id`. */
  attestor_role?: AttestorRole;
  /** Required when attestor_role === 'stipulated_order' (the court order id). */
  order_id?: string;
}

export interface MitigationAttestationResponse {
  claim_id: string;
  interpretation: InterpretationObject | null;
  p_proven: PProven;
  cleared: boolean;
  persisted: boolean;
}

type ReqOptions = { headers?: Record<string, string>; skipAuth?: boolean };

/** Read the stored interpretation + p_proven. Never throws for a missing object —
 * the backend returns `{ interpretation: null, p_proven: {} }`. A 403/404/network
 * error surfaces to the caller (let the card render its own empty state). */
export async function getInterpretation(
  caseId: string,
  options?: ReqOptions,
): Promise<InterpretationResponse> {
  return api.get<InterpretationResponse>(
    `/api/v1/claims/${caseId}/interpretation`,
    { showErrorToast: false, ...options },
  );
}

// ── candidate-cite read queue (READ-ONLY; never attests) ───────────────────────
// The system says what CAN be reviewed; only the accountant says what is PROVEN.
// Every candidate is a VERBATIM quote from a real uploaded doc; `page` is ALWAYS null
// (confirm-on-open — the accountant sets the real page at attest time); NOTHING here
// sets `p`. Backend: GET /api/v1/claims/{id}/interpretation/candidates.

/** One staged candidate cite. `page` is always null; `match_confidence` is lexical
 * match strength, NOT a proof probability. `status` is always 'candidate'. */
export interface CandidateCite {
  status: 'candidate';
  factor_id: MitigationFactorId;
  doc_id: string;
  source_label: string;
  document_type?: string | null;
  page: null;
  quote: string;
  matched_term: string;
  hit_count: number;
  match_confidence: number;
  why: string;
  rag_grounded: boolean;
}

/** RAG-grounded per-factor proof STANDARD (enrichment; may be null on a corpus gap). */
export interface FactorStandard {
  factor_id: string;
  text: string;
  citations: { source_file: string; score: number; rank: number }[];
  grounding: { claims_count: number; claims_grounded: number; claims_ungrounded: number };
  corpus_gap: boolean;
  rag_grounded: boolean;
  grounding_flags: string[];
}

export interface FactorCandidates {
  factor_id: MitigationFactorId;
  label: string;
  standard: FactorStandard | null;
  candidates: CandidateCite[];
}

export interface CandidateStaging {
  claim_id: string;
  generated_at: string;
  disclaimer: string;
  rag: { configured: boolean; standards_available: number; timed_out: boolean };
  /** Always 5 rows in factor order; an empty `candidates` IS the honest 'nothing
   * staged — carrier must prove' state (do NOT invent a cite). */
  by_factor: FactorCandidates[];
}

/** Read the staged candidate cites for the mitigation levers — the read queue the
 * accountant reads-and-agrees to. READ-ONLY: no candidate sets `p`, and picking one
 * only pre-fills the attest composer (the human still arms + confirms). Optional
 * `factor` limits to one lever. Never throws for a missing case object (returns 5
 * honest empty rows); a 403/404/network error surfaces to the caller. */
export async function getInterpretationCandidates(
  caseId: string,
  factor?: MitigationFactorId,
  options?: ReqOptions,
): Promise<CandidateStaging> {
  const qs = factor ? `?factor=${encodeURIComponent(factor)}` : '';
  return api.get<CandidateStaging>(
    `/api/v1/claims/${caseId}/interpretation/candidates${qs}`,
    { showErrorToast: false, ...options },
  );
}

/** Attest (p=1, with cite) or clear (p=0) ONE lever. The board moves on the next
 * brief GET. Throws on 4xx (403 role, 409 third-party, 422 bad body) — the error
 * message is the JSON-stringified FastAPI `detail`. */
export async function patchMitigationAttestation(
  caseId: string,
  body: MitigationAttestationBody,
  options?: ReqOptions,
): Promise<MitigationAttestationResponse> {
  return api.patch<MitigationAttestationResponse>(
    `/api/v1/claims/${caseId}/interpretation/mitigation`,
    body,
    { showErrorToast: false, ...options },
  );
}
